import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import LoadingV2 from '@/components/Loading';
import { TableV2 } from '@/components/TableV2';
import SubmitButton from '@/ctComponents/submitButton';
import CloseButton from '@/ctComponents/closeButton';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import abStyles from '@/screens/Autobook/containerList.module.css';
import DateSelector from './DateSelector';
import {
  getTerminalHistoriesIndex,
  getTerminalHistoriesByTerminal,
  getTerminalHistoryDetail,
  SaveApiResMessage,
} from '@/actions';

function formatHistoryTs(iso) {
  if (!iso) return '—';
  return moment.utc(iso).local().format('ddd MM/DD hh:mm A');
}

function formatTabDate(isoDate) {
  if (!isoDate) return '';
  return moment(isoDate, 'YYYY-MM-DD').format('ddd MM/DD');
}

function shiftToLabel(v) {
  if (v === true || v === 1) return { text: 'YES', className: 'text-green-600' };
  if (v === false || v === 0) return { text: 'NO', className: 'text-red-600' };
  if (v === 'DUAL' || v === 2)
    return { text: 'DUAL', className: 'text-amber-600' };
  return { text: '—', className: 'text-gray-400' };
}

function normalizeShift(v) {
  if (v === true || v === 1) return 1;
  if (v === false || v === 0) return 0;
  if (v === 'DUAL' || v === 2) return 2;
  if (v === undefined || v === null) return null;
  return v;
}

function shiftsDiffer(a, b) {
  return normalizeShift(a) !== normalizeShift(b);
}

function getFirstSourceUrl(sources) {
  if (!sources) return null;
  const list = Array.isArray(sources) ? sources : [sources];
  if (list.length === 0) return null;
  const s = list[0];
  if (typeof s === 'string') {
    if (s.startsWith('http') || s.startsWith('data:')) return s;
    return s;
  }
  if (s?.url) return s.url;
  if (s?.src) return s.src;
  if (s?.path) return s.path;
  if (s?.base64) {
    const mime = s.mime || s.type || 'image/png';
    return `data:${mime};base64,${s.base64}`;
  }
  return null;
}

function toImageProxyUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  const cleaned = rawUrl.trim();
  if (cleaned.startsWith('data:')) return cleaned;
  if (cleaned.startsWith('/images/')) return cleaned;
  if (cleaned.startsWith('/storage/')) {
    return cleaned.replace('/storage/', '/images/');
  }

  // Generic fallback for any absolute/relative URL containing /storage/
  const marker = '/storage/';
  const markerIdx = cleaned.indexOf(marker);
  if (markerIdx >= 0) {
    const tail = cleaned.slice(markerIdx + marker.length);
    const [pathPart, hashPart] = tail.split('#');
    return `/images/${pathPart.replace(/^\/+/, '')}`;
  }

  try {
    const parsed = new URL(cleaned);
    const idx = parsed.pathname.indexOf(marker);
    if (idx >= 0) {
      const tail = parsed.pathname.slice(idx + marker.length);
      return `/images/${tail}${parsed.search || ''}`;
    }
  } catch (_err) {
    // Keep original if URL parsing fails
  }

  return cleaned;
}

function extensionFromImageMime(mime) {
  if (!mime) return 'png';
  if (mime.includes('jpeg')) return 'jpg';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('gif')) return 'gif';
  if (mime.includes('svg')) return 'svg';
  return 'png';
}

function ShiftCell({ value, muted, highlight }) {
  const { text, className } = shiftToLabel(value);
  return (
    <span
      className={`font-semibold ${muted ? 'text-gray-500' : className}`}>
      {text}
    </span>
  );
}

function getContainerAt(detail, date, ssl, key) {
  return detail?.data_new?.[date]?.[ssl]?.containers?.[key];
}

function resolvePreviousDateKey(detail, previousDetail, selectedDate, ssl) {
  if (!previousDetail?.data_new || !selectedDate || !ssl) return selectedDate;
  const dn = previousDetail.data_new;
  const hasSsl = (dk) => dk && dn[dk]?.[ssl] != null;

  if (hasSsl(selectedDate)) return selectedDate;

  const curIdx = detail?.dates?.indexOf(selectedDate);
  if (curIdx >= 0 && previousDetail.dates?.[curIdx]) {
    const byIdx = previousDetail.dates[curIdx];
    if (hasSsl(byIdx)) return byIdx;
  }

  for (const dk of Object.keys(dn).sort()) {
    if (hasSsl(dk)) return dk;
  }

  return selectedDate;
}

function containerRowChanged(
  previousDetail,
  previousDateKey,
  ssl,
  itemKey,
  currRow
) {
  const prevC = previousDetail
    ? getContainerAt(previousDetail, previousDateKey, ssl, itemKey)
    : null;
  return (
    shiftsDiffer(prevC?.shift1, currRow?.shift1) ||
    shiftsDiffer(prevC?.shift2, currRow?.shift2)
  );
}

function sslKeysForDropdown(dateBlock) {
  if (!dateBlock || typeof dateBlock !== 'object') return [];
  return Object.keys(dateBlock).filter((k) => dateBlock[k]?.has);
}

const AB_BORDER = '#E1E4EA';
const AB_RADIUS_CARD = 8;
const AB_RADIUS_CONTROL = 4;
const PAD_CARD = 'px-5 py-4';
const PAD_SIDEBAR_HEADER = 'px-4 py-3.5';
const PAD_SIDEBAR_BODY = 'px-4 py-3';
const PAD_SCREENSHOT_TOOLBAR = 'px-5 pt-4 pb-3.5';
const PAD_SCREENSHOT_PREVIEW = 'px-5 pb-5 pt-4';
const PAD_COMPARE_ROW = 'px-4 py-3';
const PAD_TABLE_SCROLL = 'px-5 pb-5 pt-4';

function cardFrameStyle() {
  return {
    border: `1px solid ${AB_BORDER}`,
    borderRadius: AB_RADIUS_CARD,
    boxSizing: 'border-box',
  };
}

export default function ReceivablesHistoryPanel({ open, terminalId }) {
  const [terminalsIndex, setTerminalsIndex] = useState([]);
  const [histories, setHistories] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSsl, setSelectedSsl] = useState(null);

  const [compareMode, setCompareMode] = useState(false);
  const [showChangesOnly, setShowChangesOnly] = useState(false);
  const [previousDetail, setPreviousDetail] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  const [dataViewMode, setDataViewMode] = useState(false);

  const pageRef = useRef(1);
  const lastPageRef = useRef(1);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    lastPageRef.current = lastPage;
  }, [lastPage]);

  useEffect(() => {
    setDataViewMode(false);
  }, [selectedUuid]);

  const terminalName = useMemo(() => {
    const t = terminalsIndex.find((x) => x.id === terminalId);
    return t?.slug || 'Terminal';
  }, [terminalsIndex, terminalId]);

  const resetState = useCallback(() => {
    setTerminalsIndex([]);
    setHistories([]);
    setPage(1);
    setLastPage(1);
    pageRef.current = 1;
    lastPageRef.current = 1;
    setSelectedUuid(null);
    setDetail(null);
    setSelectedDate(null);
    setSelectedSsl(null);
    setCompareMode(false);
    setShowChangesOnly(false);
    setPreviousDetail(null);
    setDataViewMode(false);
  }, []);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  useEffect(() => {
    if (!open || !terminalId) return undefined;

    let cancelled = false;

    const run = async () => {
      setLoadingInitial(true);
      setDetail(null);
      setHistories([]);
      setSelectedUuid(null);
      try {
        const indexRes = await getTerminalHistoriesIndex();
        if (cancelled) return;
        setTerminalsIndex(indexRes.data?.terminals ?? []);

        const listRes = await getTerminalHistoriesByTerminal(terminalId, 1);
        if (cancelled) return;
        const paginated = listRes.data?.histories;
        const rows = paginated?.data ?? [];
        setHistories(rows);
        const p1 = paginated?.current_page ?? 1;
        const lp1 = paginated?.last_page ?? 1;
        setPage(p1);
        setLastPage(lp1);
        pageRef.current = p1;
        lastPageRef.current = lp1;

        if (rows.length > 0) {
          const uuid = rows[0].uuid;
          setSelectedUuid(uuid);
          const detailRes = await getTerminalHistoryDetail(uuid);
          if (cancelled) return;
          const h = detailRes.data?.history;
          setDetail(h);
          const d0 = h?.dates?.[0];
          if (d0 && h.data_new?.[d0]) {
            setSelectedDate(d0);
            const sslKeys = sslKeysForDropdown(h.data_new[d0]);
            setSelectedSsl(sslKeys[0] ?? null);
          } else {
            setSelectedDate(null);
            setSelectedSsl(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          SaveApiResMessage({
            title: 'Error',
            message: err?.message || String(err),
            mode: 'warning',
          });
        }
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, terminalId]);

  const loadDetail = useCallback(async (uuid) => {
    if (!uuid) return;
    setLoadingDetail(true);
    try {
      const detailRes = await getTerminalHistoryDetail(uuid);
      const h = detailRes.data?.history;
      setDetail(h);
      const d0 = h?.dates?.[0];
      if (d0 && h.data_new?.[d0]) {
        setSelectedDate(d0);
        const sslKeys = sslKeysForDropdown(h.data_new[d0]);
        setSelectedSsl(sslKeys[0] ?? null);
      } else {
        setSelectedDate(h?.dates?.[0] ?? null);
        setSelectedSsl(null);
      }
    } catch (err) {
      SaveApiResMessage({
        title: 'Error',
        message: err?.message || String(err),
        mode: 'warning',
      });
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const onSelectHistory = useCallback(
    (uuid) => {
      if (!uuid || uuid === selectedUuid) return;
      setCompareMode(false);
      setShowChangesOnly(false);
      setLoadingCompare(false);
      setSelectedUuid(uuid);
      loadDetail(uuid);
    },
    [selectedUuid, loadDetail]
  );

  const loadMore = useCallback(async () => {
    if (!terminalId || loadingMore) return;
    const nextPage = pageRef.current + 1;
    if (nextPage > lastPageRef.current) return;
    setLoadingMore(true);
    try {
      const listRes = await getTerminalHistoriesByTerminal(
        terminalId,
        nextPage
      );
      const paginated = listRes.data?.histories;
      const nextRows = paginated?.data ?? [];
      setHistories((prev) => [...prev, ...nextRows]);
      const cp = paginated?.current_page ?? nextPage;
      const lp = paginated?.last_page ?? lastPageRef.current;
      setPage(cp);
      setLastPage(lp);
      pageRef.current = cp;
      lastPageRef.current = lp;
    } catch (err) {
      SaveApiResMessage({
        title: 'Error',
        message: err?.message || String(err),
        mode: 'warning',
      });
    } finally {
      setLoadingMore(false);
    }
  }, [terminalId, loadingMore]);

  const onTimelineScroll = useCallback(
    (e) => {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
        loadMore();
      }
    },
    [loadMore]
  );

  const comparePrevious = useMemo(() => {
    const i = histories.findIndex((h) => h.uuid === selectedUuid);
    if (i < 0 || i >= histories.length - 1) return null;
    return histories[i + 1];
  }, [histories, selectedUuid]);

  useEffect(() => {
    if (!compareMode || !comparePrevious?.uuid) {
      setPreviousDetail(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingCompare(true);
      try {
        const res = await getTerminalHistoryDetail(comparePrevious.uuid);
        if (!cancelled) setPreviousDetail(res.data?.history ?? null);
      } catch (err) {
        if (!cancelled) {
          SaveApiResMessage({
            title: 'Error',
            message: err?.message || String(err),
            mode: 'warning',
          });
          setPreviousDetail(null);
        }
      } finally {
        if (!cancelled) setLoadingCompare(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [compareMode, comparePrevious?.uuid]);

  const sslOptions = useMemo(() => {
    if (!detail?.data_new || !selectedDate) return [];
    const block = detail.data_new[selectedDate];
    return sslKeysForDropdown(block);
  }, [detail, selectedDate]);

  const sslSelectOptions = useMemo(
    () =>
      sslOptions.map((k) => ({
        label: detail?.data_new?.[selectedDate]?.[k]?.name || k,
        value: k,
      })),
    [sslOptions, detail, selectedDate]
  );

  useEffect(() => {
    setSelectedSsl((prev) =>
      sslOptions.includes(prev) ? prev : sslOptions[0] ?? null
    );
  }, [sslOptions]);

  const sslSelectValue = useMemo(() => {
    if (!selectedSsl) return null;
    const label =
      detail?.data_new?.[selectedDate]?.[selectedSsl]?.name || selectedSsl;
    return { label, value: selectedSsl };
  }, [selectedSsl, detail, selectedDate]);

  const historyDateOptions = useMemo(() => {
    if (!detail?.dates?.length) return [];
    return detail.dates.map((d) => ({
      key: d,
      dayName: moment(d, 'YYYY-MM-DD').format('ddd'),
      dayNumber: moment(d, 'YYYY-MM-DD').format('DD'),
      month: moment(d, 'YYYY-MM-DD').format('MMM'),
    }));
  }, [detail]);

  const comparePreviousDateKey = useMemo(
    () =>
      resolvePreviousDateKey(
        detail,
        previousDetail,
        selectedDate,
        selectedSsl
      ),
    [detail, previousDetail, selectedDate, selectedSsl]
  );

  const containerRows = useMemo(() => {
    if (!detail?.data_new || !selectedDate || !selectedSsl) return [];
    const ssl = detail.data_new[selectedDate]?.[selectedSsl];
    const containers = ssl?.containers;
    if (!containers || typeof containers !== 'object') return [];
    return Object.keys(containers)
      .map((k) => ({ key: k, row: containers[k] }))
      .sort((a, b) =>
        (a.row?.title || a.key).localeCompare(b.row?.title || b.key)
      );
  }, [detail, selectedDate, selectedSsl]);

  const tablePayload = useMemo(() => {
    if (!detail || !selectedDate || !selectedSsl) {
      return { headers: [], data: [] };
    }

    const rows = containerRows.filter((item) => {
      if (!compareMode || !showChangesOnly) return true;
      if (!previousDetail) return false;
      return containerRowChanged(
        previousDetail,
        comparePreviousDateKey,
        selectedSsl,
        item.key,
        item.row
      );
    });

    let headers = [];
    if (!compareMode) {
      headers = [
        { key: 'size', label: 'Size' },
        { key: 's1', label: '1st' },
        { key: 's2', label: '2nd' },
      ];
    } else {
      headers = [
        { key: 'size', label: 'Size' },
        { key: 'p1', label: 'Prev 1st' },
        { key: 'n1', label: 'Now 1st' },
        { key: 'p2', label: 'Prev 2nd' },
        { key: 'n2', label: 'Now 2nd' },
      ];
    }

    const data = rows.map((item, index) => {
      const curr = item.row;
      const prev = previousDetail
        ? getContainerAt(
            previousDetail,
            comparePreviousDateKey,
            selectedSsl,
            item.key
          )
        : null;

      const cellData = [];
      cellData.push(<span className="font-medium">{curr?.title || item.key}</span>);

      if (!compareMode) {
        cellData.push(<ShiftCell key="c1" value={curr?.shift1} />);
        cellData.push(<ShiftCell key="c2" value={curr?.shift2} />);
      } else {
        const ch1 = shiftsDiffer(prev?.shift1, curr?.shift1);
        const ch2 = shiftsDiffer(prev?.shift2, curr?.shift2);
        cellData.push(
          <ShiftCell key="p1" value={prev?.shift1} muted highlight={false} />
        );
        cellData.push(
          <ShiftCell key="n1" value={curr?.shift1} highlight={ch1} />
        );
        cellData.push(
          <ShiftCell key="p2" value={prev?.shift2} muted highlight={false} />
        );
        cellData.push(
          <ShiftCell key="n2" value={curr?.shift2} highlight={ch2} />
        );
      }

      return { id: `${item.key}-${index}`, cellData };
    });

    return { headers, data };
  }, [
    detail,
    selectedDate,
    selectedSsl,
    containerRows,
    compareMode,
    showChangesOnly,
    previousDetail,
    comparePreviousDateKey,
  ]);

  const screenshotUrl = detail
    ? toImageProxyUrl(getFirstSourceUrl(detail.sources))
    : null;

  const downloadScreenshot = useCallback(async () => {
    if (!screenshotUrl) return;
    try {
      const res = await fetch(screenshotUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext = extensionFromImageMime(
        blob.type || res.headers.get('content-type')
      );
      const safeName = (terminalName || 'terminal').replace(/[^\w-]+/g, '_');
      const stamp = detail?.updated_at
        ? moment.utc(detail.updated_at).local().format('YYYY-MM-DD_HHmmss')
        : String(Date.now());
      const filename = `${safeName}_screenshot_${stamp}.${ext}`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      SaveApiResMessage({
        title: 'Error',
        message: err?.message || 'Could not download image',
        mode: 'warning',
      });
    }
  }, [screenshotUrl, terminalName, detail?.updated_at]);

  if (!open || !terminalId) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
        {!terminalId ? 'Terminal not selected.' : null}
      </div>
    );
  }

  if (loadingInitial) {
    return (
      <div className="flex min-h-[320px] flex-1 items-center justify-center">
        <LoadingV2 />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-0 font-['Averta'] text-[#131517]">
      <div className="flex min-h-0 flex-1 flex-row gap-5">
        <div
          className="box-border flex h-full min-h-0 flex-col"
          style={{ minWidth: '305px', width: '30%', maxWidth: '400px' }}>
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white"
            style={cardFrameStyle()}>
            <div
              className={`shrink-0 ${PAD_SIDEBAR_HEADER}`}
              style={{
                borderBottom: `1px solid ${AB_BORDER}`,
                boxSizing: 'border-box',
              }}>
              <h2
                className="m-0 !text-[18px] font-bold leading-tight text-[#131517]"
                style={{ fontSize: '18px', lineHeight: 1.2 }}>
                History Timeline
              </h2>
            </div>
            <div
              className={`flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto ${PAD_SIDEBAR_BODY}`}
              style={{ maxHeight: 'none' }}
              onScroll={onTimelineScroll}>
              {histories.length === 0 ? (
                <p className="m-0 px-1 py-6 text-center text-[11px] leading-snug text-[#99A0AE]">
                  No history entries.
                </p>
              ) : (
                histories.map((h, index) => {
                  const active = h.uuid === selectedUuid;
                  return (
                    <div
                      key={h.uuid}
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelectHistory(h.uuid)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          onSelectHistory(h.uuid);
                      }}
                      className={`${
                        active
                          ? abStyles.containerItemSelected
                          : abStyles.containerItem
                      } outline-none focus-visible:ring-2 focus-visible:ring-[#335CFF] focus-visible:ring-offset-1`}
                      style={{
                        height: 'auto',
                        minHeight: 28,
                        alignItems: 'flex-start',
                        backgroundColor: '#fff',
                        borderColor: active ? '#335CFF' : AB_BORDER,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderRadius: AB_RADIUS_CONTROL,
                        padding: '0px !important',
                      }}>
                      <div
                        className={abStyles.containerItemWrapper}
                        style={{
                          width: '100%',
                          alignItems: 'center',
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className={abStyles.appointmentDetails}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: 0,
                              padding: '0px',
                            }}>
                            <span
                              className={abStyles.time}
                              style={{ fontSize: '12px' }}>
                              {formatHistoryTs(h.created_at)} →{' '}
                              {index === 0
                                ? 'Present'
                                : formatHistoryTs(h.updated_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {loadingMore ? (
                <div className="py-3 text-center text-[11px] text-[#99A0AE]">
                  Loading…
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Detail */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-5 overflow-hidden">
          {loadingDetail ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <LoadingV2 />
            </div>
          ) : detail ? (
            <>
              <header
                className={`shrink-0 bg-white ${PAD_CARD}`}
                style={cardFrameStyle()}>
                <h1
                  className="m-0 max-w-full truncate font-bold leading-snug text-[#131517]"
                  style={{ fontSize: '14px', lineHeight: 1.3 }}>
                  {terminalName + ' Terminal'}
                </h1>
                <p
                  className="mt-1 m-0 font-normal leading-normal text-[#717784]"
                  style={{ fontSize: '12px', lineHeight: 1.35 }}>
                  {formatHistoryTs(detail.created_at)} →{' '}
                  {formatHistoryTs(detail.updated_at)}
                </p>
              </header>

              <section
                className="flex shrink-0 flex-col overflow-hidden bg-white"
                style={cardFrameStyle()}>
                <div
                  className={`flex shrink-0 flex-row items-center justify-between gap-3 ${PAD_SCREENSHOT_TOOLBAR}`}
                  style={{
                    borderBottom: `1px solid ${AB_BORDER}`,
                    boxSizing: 'border-box',
                  }}>
                  <p
                    className="m-0 font-semibold text-[#131517]"
                    style={{ fontSize: '14px', lineHeight: 1.3 }}>
                    Source Screenshot
                  </p>
                  <div className="flex shrink-0 items-center gap-2">
                    <CloseButton
                      text="Download"
                      textClassName="!text-[12px]"
                      textStyle={{ fontSize: '12px', lineHeight: 1.2 }}
                      buttonStyle={{
                        width: '100px',
                        minWidth: '100px',
                        maxWidth: '100px',
                        height: '30px',
                      }}
                      onClick={downloadScreenshot}
                      disable={!screenshotUrl}
                      width="100px"
                      className="!px-2 !py-1 "
                    />
                    <SubmitButton
                      text={dataViewMode ? 'Full View' : 'Data View'}
                      textClassName="!text-[12px]"
                      textStyle={{ fontSize: '12px', lineHeight: 1.2 }}
                      buttonStyle={{
                        width: '100px',
                        minWidth: '100px',
                        maxWidth: '100px',
                        height: '30px',
                      }}
                      onClick={() => setDataViewMode((v) => !v)}
                      width="100px"
                      className="!min-h-0 !px-2 !py-1 "
                    />
                  </div>
                </div>
                <div
                  className={`overflow-auto bg-[#F2F5F8] transition-all duration-200 ease-out ${PAD_SCREENSHOT_PREVIEW} ${
                    dataViewMode
                      ? 'h-44 max-h-44 min-h-0 shrink-0'
                      : 'max-h-[min(48vh,400px)] min-h-[160px] shrink-0'
                  }`}
                  style={{
                    boxSizing: 'border-box',
                    background: '#ffffff',
                    padding: '10px',
                  }}>
                  {screenshotUrl ? (
                    <img
                      src={screenshotUrl}
                      alt="Source screenshot"
                      className="block h-auto min-h-[120px] w-full max-w-none rounded-sm bg-white"
                    />
                  ) : (
                    <div className="flex min-h-[160px] w-full min-w-max items-center justify-center py-10 text-center text-[12px] text-[#717784]">
                      Screenshot Preview
                    </div>
                  )}
                </div>
              </section>

              <section
                className={`flex min-h-0 flex-col overflow-hidden bg-white ${
                  dataViewMode ? 'min-h-0 flex-1' : 'min-h-[140px] flex-1'
                }`}
                style={cardFrameStyle()}>
                <div
                  className={`shrink-0 space-y-4 ${PAD_CARD}`}
                  style={{
                    borderBottom: `1px solid ${AB_BORDER}`,
                    boxSizing: 'border-box',
                  }}>
                  <div
                    className={`flex items-center justify-between gap-3 border border-solid bg-[#F9FAFB] ${PAD_COMPARE_ROW}`}
                    style={{
                      borderColor: AB_BORDER,
                      borderWidth: 1,
                      borderRadius: AB_RADIUS_CARD,
                    }}>
                    <div className="flex min-w-0 items-center gap-3">
                      <p
                        className="m-0 whitespace-nowrap font-semibold text-[#131517]"
                        style={{ fontSize: '14px', lineHeight: 1.3 }}>
                        Compare with previous
                      </p>
                      {/* <p
                        className="m-0 truncate text-[#717784]"
                        style={{ fontSize: '12px', lineHeight: 1.3 }}>
                        {comparePrevious
                          ? `Compare ${formatHistoryTs(
                              detail.created_at
                            )} against ${formatHistoryTs(
                              comparePrevious.created_at
                            )}`
                          : 'No older snapshot available'}
                      </p> */}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <SubmitButton
                        text={compareMode ? 'Comparing' : 'Compare'}
                        textClassName="!text-[12px]"
                        textStyle={{ fontSize: '12px', lineHeight: 1.2 }}
                        buttonStyle={{
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px',
                          padding: '0px 5px',
                          height: '30px',
                        }}
                        onClick={() => {
                          let nextCompare = false;
                          setCompareMode((was) => {
                            nextCompare = !was;
                            return nextCompare;
                          });
                          setShowChangesOnly(nextCompare);
                        }}
                        disable={!comparePrevious}
                        width="100px"
                        className={`!min-h-0 !px-2 ${
                          !comparePrevious ? '!opacity-50' : ''
                        }`}
                      />
                      {compareMode ? (
                        <CloseButton
                          text={showChangesOnly ? 'Show All' : 'Changes Only'}
                          textClassName={
                            showChangesOnly
                              ? '!text-[12px] !text-white'
                              : '!text-[12px]'
                          }
                          textStyle={{ fontSize: '12px', lineHeight: 1.2 }}
                          buttonStyle={{
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px',
                            padding: '0px 5px',
                            height: '30px',
                          }}
                          onClick={() => setShowChangesOnly((prev) => !prev)}
                          width="auto"
                          className={`!h-7 !min-w-0 !px-3 !text-[11px] ${
                            showChangesOnly ? '!bg-gray-900' : ''
                          }`}
                        />
                      ) : null}
                    </div>
                  </div>

                  {historyDateOptions.length ? (
                    <DateSelector
                      textSize="11px"
                      dates={historyDateOptions}
                      selectedDate={selectedDate}
                      onDateSelect={(d) => {
                        setSelectedDate(d);
                        const keys = sslKeysForDropdown(detail.data_new?.[d]);
                        if (!keys.length) {
                          setSelectedSsl(null);
                          return;
                        }
                        setSelectedSsl((prevSsl) =>
                          prevSsl && keys.includes(prevSsl) ? prevSsl : keys[0]
                        );
                      }}
                      className="flex-wrap"
                    />
                  ) : null}

                  {sslSelectOptions.length > 0 ? (
                    <div className="w-full">
                      <CustomSelectEx
                        dropdown
                        placeholder="SSL"
                        minHeight={48}
                        value={sslSelectValue}
                        onChange={(opt) => setSelectedSsl(opt?.value ?? null)}
                        options={sslSelectOptions}
                        width="100%"
                      />
                    </div>
                  ) : null}
                </div>

                <div
                  className={`min-h-0 flex-1 ${PAD_TABLE_SCROLL} ${
                    dataViewMode ? 'overflow-y-auto' : 'overflow-hidden'
                  }`}
                >
                  {loadingCompare && compareMode ? (
                    <div className="flex justify-center py-10">
                      <LoadingV2 />
                    </div>
                  ) : (
                    <div className="[&>div]:mb-2">
                      <TableV2
                        fontSize="12px"
                        minWidth={compareMode ? 400 : 200}
                        headers={tablePayload.headers}
                        data={tablePayload.data}
                        emptyMessage={
                          showChangesOnly && compareMode
                            ? 'No changes for this filter'
                            : 'No Empty Receiving data detected for this time'
                        }
                      />
                    </div>
                  )}
                </div>
              </section>
            </>
          ) : (
            <p className="m-0 px-1 py-8 text-center text-[13px] leading-relaxed text-[#717784]">
              Select an entry from the timeline.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}



